/**
 * i18n Configuration
 * SpecReferenceID: FRONTEND-WEB-V1
 * Requirements: FRONTEND-WEB-V1-R11
 *
 * Internationalization configuration using i18next.
 * Supports English (default), German, and French.
 *
 * FRONTEND-WEB-V1-R11: Provide internationalization support for UI.
 *
 * Risk Links: R11
 * FMEA Links: FM11
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpBackend from 'i18next-http-backend';

// Import translation files directly for bundling
import enTranslations from './locales/en.json';
import deTranslations from './locales/de.json';
import frTranslations from './locales/fr.json';

/**
 * Supported languages.
 * FRONTEND-WEB-V1-R11: Define supported locales.
 */
export const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'de', name: 'German', nativeName: 'Deutsch' },
  { code: 'fr', name: 'French', nativeName: 'Français' },
] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number]['code'];

export const DEFAULT_LANGUAGE: SupportedLanguage = 'en';

/**
 * Initialize i18next with React integration.
 * FRONTEND-WEB-V1-R11: Configure i18n for multi-language support.
 */
void i18n
  .use(LanguageDetector)
  .use(HttpBackend)
  .use(initReactI18next)
  .init({
    // Bundled resources for offline support
    resources: {
      en: { translation: enTranslations },
      de: { translation: deTranslations },
      fr: { translation: frTranslations },
    },

    // Fallback language
    fallbackLng: DEFAULT_LANGUAGE,

    // Supported languages
    supportedLngs: SUPPORTED_LANGUAGES.map((lang) => lang.code),

    // Language detection options
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      lookupLocalStorage: 'galen-portal-language',
      caches: ['localStorage'],
    },

    // Interpolation config
    interpolation: {
      escapeValue: false, // React already escapes values
    },

    // React-specific options
    react: {
      useSuspense: true,
    },

    // Debug mode in development
    debug: import.meta.env.DEV,
  });

export default i18n;
